import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Link2, Trash2, ExternalLink, Loader2, Mail, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import api from '../../api';

export default function GoogleAccountsPanel({ isSettings = false }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAccounts = async () => {
    try {
      const res = await api.get('/api/google-accounts');
      setAccounts(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchAccounts(); }, []);

  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'google-auth-success') {
        toast.success('Google account connected!');
        fetchAccounts();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const connectGoogle = async () => {
    try {
      const res = await api.get('/api/auth/google/url');
      window.open(res.data.url, 'google-auth', 'width=500,height=600,left=400,top=200');
    } catch (err) {
      toast.error('Failed to get Google auth URL');
    }
  };

  const removeAccount = async (id) => {
    try {
      await api.delete(`/api/google-accounts/${id}`);
      setAccounts(prev => prev.filter(a => a.id !== id));
      toast.success('Account removed');
    } catch { toast.error('Failed to remove'); }
  };

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="google-accounts-panel">
      {!isSettings && (
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-lg font-bold text-zinc-800 dark:text-zinc-100 uppercase tracking-wider">Google Accounts</h2>
            <p className="text-xs text-zinc-500">Connect Google accounts for Calendar sync</p>
          </div>
          <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={connectGoogle} data-testid="connect-google-btn">
            <Link2 className="w-3.5 h-3.5 mr-1" /> Connect
          </Button>
        </div>
      )}

      {isSettings && (
        <div className="flex items-center justify-between">
           <div>
              <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">Connected Accounts</h3>
              <p className="text-xs text-zinc-500">Google accounts authorized for calendar sync</p>
           </div>
           <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={connectGoogle}>
            <Link2 className="w-3.5 h-3.5 mr-1.5" /> Connect Account
          </Button>
        </div>
      )}

      <div className="grid gap-3">
        {accounts.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-zinc-200 dark:border-zinc-800 rounded-xl">
            <div className="w-12 h-12 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mx-auto mb-3">
              <Link2 className="w-6 h-6 text-zinc-400" />
            </div>
            <p className="text-xs text-zinc-400">No accounts connected yet</p>
          </div>
        ) : (
          accounts.map((account, i) => (
            <div key={account.id} className="data-card !p-4 flex items-center justify-between" data-testid={`google-account-${account.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-600/10 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-medium text-zinc-800 dark:text-zinc-100 line-clamp-1">{account.email}</h4>
                    {account.is_primary && <Badge className="text-[0.55rem] h-4 bg-emerald-100 text-emerald-700 border-none">Primary</Badge>}
                  </div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    <span className="text-[0.6rem] text-emerald-600">Sync Active</span>
                  </div>
                </div>
              </div>
              <button className="w-8 h-8 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center text-zinc-400 hover:text-red-600 transition-colors shrink-0" onClick={() => removeAccount(account.id)}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>

      <div className="mt-6 p-4 rounded-xl bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20 flex gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-400">About Google Integration</h4>
          <p className="text-[0.7rem] text-blue-600/80 dark:text-blue-500 leading-relaxed">
            Connecting your account allows the system to read your calendar availability and push scheduled events directly to your Google Calendar.
          </p>
          <p className="text-[0.7rem] text-blue-600/80 dark:text-blue-500 leading-relaxed font-medium bg-white/40 dark:bg-black/20 p-2 rounded-md border border-blue-200/50">
            <b>Important:</b> This application only manages calendars it creates. We recommend one Google account for every ~25 calendars to avoid API rate limits. All data is processed securely and only used for your scheduling needs.
          </p>
        </div>
      </div>
    </div>
  );
}

